import React from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  Typography,
  useTheme,
} from '@mui/material'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import BusinessIcon from '@mui/icons-material/Business'
import PeopleIcon from '@mui/icons-material/People'
import SpeedIcon from '@mui/icons-material/Speed'
import Footer from 'components/Layout/Footer'
import { useHandleChangeRoute } from 'data/hooks'

const FEATURES = [
  {
    icon: <PeopleIcon sx={{ fontSize: 40 }} />,
    title: 'Access Verified Candidates',
    description: 'Connect directly with qualified, credentialed healthcare professionals actively seeking new opportunities.',
  },
  {
    icon: <SpeedIcon sx={{ fontSize: 40 }} />,
    title: 'Post Jobs in Minutes',
    description: 'Create job postings and start seeing matched candidates right away.',
  },
  {
    icon: <BusinessIcon sx={{ fontSize: 40 }} />,
    title: 'No Third-Party Fees',
    description: 'Hire directly without recruiter markups or staffing agency fees cutting into your budget.',
  },
]

const TESTIMONIALS = [
  {
    quote: 'MyStethi helped us fill a critical hospitalist role in under two weeks. The candidate quality was outstanding.',
    author: 'Chief Medical Officer',
    organization: 'Regional Medical Center',
  },
  {
    quote: 'We\'ve tried other platforms, but MyStethi\'s matching technology surfaces candidates that are genuinely interested and qualified.',
    author: 'Director of Physician Recruitment',
    organization: 'Academic Health System',
  },
]

const EmployerLandingPage = () => {
  const theme = useTheme()
  const handleChangeRoute = useHandleChangeRoute()

  return (
    <Box>
      <Box
        sx={{
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          py: { xs: 8, md: 12 },
        }}
      >
        <Container maxWidth='md'>
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant='h2'
              sx={{
                fontWeight: 700,
                mb: 3,
                fontSize: { xs: '2rem', md: '3rem' },
              }}
            >
              Find the Right Physicians for Your Organization
            </Typography>
            <Typography
              variant='h6'
              sx={{
                mb: 4,
                opacity: 0.9,
                fontWeight: 400,
                maxWidth: 600,
                mx: 'auto',
              }}
            >
              Post jobs and get matched with qualified candidates in minutes. No recruiter fees, no middlemen.
            </Typography>
            <Button
              variant='contained'
              size='large'
              onClick={() => handleChangeRoute('/demo')()}
              sx={{
                backgroundColor: theme.palette.common.white,
                color: theme.palette.primary.main,
                fontWeight: 700,
                px: 5,
                py: 2,
                fontSize: '1.1rem',
                textTransform: 'none',
                borderRadius: '8px',
                '&:hover': {
                  backgroundColor: theme.palette.grey[100],
                },
              }}
            >
              Request a Demo
            </Button>
          </Box>
        </Container>
      </Box>

      <Container maxWidth='lg' sx={{ py: { xs: 6, md: 10 } }}>
        <Typography
          variant='h4'
          align='center'
          fontWeight={700}
          sx={{ mb: 6 }}
        >
          Everything you need to hire smarter
        </Typography>
        <Grid container spacing={4}>
          {FEATURES.map(feature => (
            <Grid item xs={12} md={4} key={feature.title}>
              <Card
                variant='outlined'
                sx={{
                  height: '100%',
                  borderRadius: '12px',
                  p: 1,
                }}
              >
                <CardContent>
                  <Box sx={{ color: theme.palette.primary.main, mb: 2 }}>
                    {feature.icon}
                  </Box>
                  <Typography variant='h6' fontWeight={700} gutterBottom>
                    {feature.title}
                  </Typography>
                  <Typography variant='body2' color='text.secondary'>
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      <Box sx={{ backgroundColor: theme.palette.grey[50], py: { xs: 6, md: 10 } }}>
        <Container maxWidth='lg'>
          <Typography
            variant='h4'
            align='center'
            fontWeight={700}
            sx={{ mb: 6 }}
          >
            Trusted by Healthcare Organizations
          </Typography>
          <Grid container spacing={4}>
            {TESTIMONIALS.map(testimonial => (
              <Grid item xs={12} md={6} key={testimonial.author}>
                <Card
                  variant='outlined'
                  sx={{ height: '100%', borderRadius: '12px' }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <CheckCircleOutlineIcon
                      sx={{ color: theme.palette.success.main, mb: 2 }}
                    />
                    <Typography
                      variant='body1'
                      sx={{ mb: 3, fontStyle: 'italic', lineHeight: 1.7 }}
                    >
                      "{testimonial.quote}"
                    </Typography>
                    <Typography variant='subtitle2' fontWeight={700}>
                      {testimonial.author}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                      {testimonial.organization}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Box sx={{ py: { xs: 6, md: 10 }, textAlign: 'center' }}>
        <Container maxWidth='sm'>
          <Typography variant='h4' fontWeight={700} gutterBottom>
            Ready to find your next hire?
          </Typography>
          <Typography variant='body1' color='text.secondary' sx={{ mb: 4 }}>
            Join healthcare organizations already using MyStethi to connect with top physician talent.
          </Typography>
          <Button
            variant='contained'
            size='large'
            onClick={() => handleChangeRoute('/demo')()}
            sx={{
              fontWeight: 700,
              px: 5,
              py: 2,
              fontSize: '1.1rem',
              textTransform: 'none',
              borderRadius: '8px',
            }}
          >
            Request a Demo
          </Button>
        </Container>
      </Box>
      <Footer />
    </Box>
  )
}

export default EmployerLandingPage
